import { useCallback, useEffect, useState } from 'react'
import { useTranslations } from '@/core/translation'
import { BASE_CURRENCIES, Currencies, isBaseCurrency, usePackageInfo } from '@/utils'
import { CurrencyAndLocationModalProps } from './CurrencyAndLocationModal.types'

interface UseCurrencyAndLocationModalControllerProps extends Pick<
  CurrencyAndLocationModalProps,
  'data' | 'onClose' | 'onSelectCurrency'
> {}

export const useCurrencyAndLocationModalController = ({
  data,
  onClose,
  onSelectCurrency,
}: UseCurrencyAndLocationModalControllerProps) => {
  const [draftCurrency, setDraftCurrency] = useState<Currencies>(data.value)

  const t = useTranslations('components')
  const { isInsidePackageFlow, currentPackage } = usePackageInfo()

  const packageCurrency = currentPackage?.cartInfo.currency ?? BASE_CURRENCIES.USD

  // if currency is not one of the supported base currencies, default to USD
  const isPackageBaseCurrency = isBaseCurrency(packageCurrency)
  const isSelectedBaseCurrency = isBaseCurrency(draftCurrency)
  const chargeCurrency = isPackageBaseCurrency ? packageCurrency : BASE_CURRENCIES.USD

  const onSelectDraft = useCallback((currency: Currencies) => {
    setDraftCurrency(currency)
  }, [])

  const onConfirm = useCallback(() => {
    onSelectCurrency(draftCurrency)
    onClose()
  }, [draftCurrency, onClose, onSelectCurrency])

  // discard the unsaved draft and restore the active currency on cancel/dismiss
  const onCancel = useCallback(() => {
    setDraftCurrency(data.value)
    onClose()
  }, [data.value, onClose])

  useEffect(() => {
    setDraftCurrency(data.value)
  }, [data.value])

  const isDiffCurrency = chargeCurrency !== draftCurrency
  const shouldShowWarning = (isInsidePackageFlow && isDiffCurrency) || !isSelectedBaseCurrency

  return {
    t,
    draftCurrency,
    chargeCurrency,
    shouldShowWarning,
    onSelectDraft,
    onConfirm,
    onCancel,
  }
}
